import { TappIcon } from '@/components/ui/TappIcon';
import { TappInput } from '@/components/ui/TappInput';
import { TappLink } from '@/components/ui/TappLink';
import { TappPasswordInput } from '@/components/ui/TappPasswordInput';
import { Button } from '@/components/ui/button';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import AppError from '../../components/AppError';
import { useAuth } from '@/lib/auth.store';
import { t } from 'i18next';

const LoginPage = () => {
  const [error, setError] = useState<any>(null);
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams] = useSearchParams();

  const redirect = searchParams.get('redirect') || '/';

  const {
    handleSubmit,
    control,
    getValues,
    formState: { isSubmitting },
  } = useForm({
    resolver: zodResolver(
      z.object({
        email: z.string().email(t('invalidEmail')),
        password: z.string().min(1, t('passwordLength')),
      })
    ),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: any) => {
    try {
      setError(null);
      await auth.login(data.email, data.password);

      // Send them back to the page they tried to visit when they were
      // redirected to the login page. Use { replace: true } so we don't create
      // another entry in the history stack for the login page.  This means that
      // when they get to the protected page and click the back button, they
      // won't end up back on the login page, which is also really nice for the
      // user experience.
      navigate(redirect, { replace: true });
    } catch (error) {
      setError(error);
    }
  };

  return (
    <main className="flex flex-col items-center justify-center px-2 py-16">
      <div className="mb-6 h-14 w-14">
        <img src="/logo_small.png" alt="Logo" />
      </div>

      <h1 className="mb-12 text-center text-2xl font-semibold tracking-tight">Bine ai venit!</h1>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full max-w-xs flex-1 flex-col items-center justify-center"
      >
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <TappInput
              className="mb-2 w-full"
              border
              type="email"
              placeholder="Email"
              prefix={<TappIcon>mail</TappIcon>}
              {...field}
              {...fieldState}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          render={({ field, fieldState }) => (
            <TappPasswordInput
              className="mb-1 w-full"
              border
              placeholder="Parolă"
              prefix={<TappIcon>lock</TappIcon>}
              {...field}
              {...fieldState}
            />
          )}
        />

        <TappLink to="/forgot-password" className="mb-6 self-end text-end text-xs leading-5">
          Ai uitat parola?
        </TappLink>

        <Button className="w-full" type="submit" disabled={isSubmitting}>
          Autentificare
        </Button>

        <AppError error={error} className="mt-4" />
      </form>
    </main>
  );
};

export default LoginPage;
